import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@exhousing/design-system";
import { useState } from "react";
import { toast } from "sonner";

import { useCreateIndexation, useIndexations } from "@/hooks/api/useFinance";

function euro(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v);
}

const today = () => new Date().toISOString().slice(0, 10);

export function IndexationCard({ contractId }: { contractId: string }) {
  const { data: indexations } = useIndexations(contractId);
  const create = useCreateIndexation(contractId);
  const [method, setMethod] = useState<"fixed" | "cpi">("fixed");
  const [pct, setPct] = useState("");
  const [baseIdx, setBaseIdx] = useState("");
  const [basePeriod, setBasePeriod] = useState("");
  const [newIdx, setNewIdx] = useState("");
  const [newPeriod, setNewPeriod] = useState("");

  const computedPct =
    method === "cpi" && Number(baseIdx) > 0 && newIdx
      ? ((Number(newIdx) - Number(baseIdx)) / Number(baseIdx)) * 100
      : null;

  function reset() {
    setPct("");
    setBaseIdx("");
    setBasePeriod("");
    setNewIdx("");
    setNewPeriod("");
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const body =
      method === "cpi"
        ? {
            method: "cpi" as const,
            cpi_base_index: Number(baseIdx),
            cpi_base_period: basePeriod || undefined,
            cpi_new_index: Number(newIdx),
            cpi_new_period: newPeriod || undefined,
            effective_date: today(),
          }
        : { method: "fixed" as const, percentage: Number(pct), effective_date: today() };
    if (method === "fixed" && !pct) return;
    if (method === "cpi" && !(Number(baseIdx) > 0 && newIdx)) return;
    create.mutate(body, {
      onSuccess: () => {
        reset();
        toast.success("Indexation applied to the contract rent");
      },
      onError: () => toast.error("Could not apply indexation"),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rent indexation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="inline-flex rounded-md border border-border p-0.5 text-sm">
          {(["fixed", "cpi"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMethod(m)}
              className={
                method === m
                  ? "rounded px-3 py-1 font-medium bg-secondary"
                  : "rounded px-3 py-1 text-muted-foreground"
              }
            >
              {m === "fixed" ? "Fixed %" : "CPI"}
            </button>
          ))}
        </div>

        <form className="flex flex-wrap items-end gap-2" onSubmit={submit}>
          {method === "fixed" ? (
            <Input
              type="number"
              step="0.1"
              placeholder="Increase %"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              className="h-10 w-32"
            />
          ) : (
            <>
              <Input
                type="number"
                step="0.001"
                placeholder="Base index"
                value={baseIdx}
                onChange={(e) => setBaseIdx(e.target.value)}
                className="h-10 w-28"
              />
              <Input
                placeholder="Base period (YYYY-MM)"
                value={basePeriod}
                onChange={(e) => setBasePeriod(e.target.value)}
                className="h-10 w-40"
              />
              <Input
                type="number"
                step="0.001"
                placeholder="New index"
                value={newIdx}
                onChange={(e) => setNewIdx(e.target.value)}
                className="h-10 w-28"
              />
              <Input
                placeholder="New period (YYYY-MM)"
                value={newPeriod}
                onChange={(e) => setNewPeriod(e.target.value)}
                className="h-10 w-40"
              />
              {computedPct !== null && (
                <span className="self-center text-sm text-muted-foreground tabular-nums">
                  = {computedPct.toFixed(2)}%
                </span>
              )}
            </>
          )}
          <Button type="submit" size="sm" variant="outline" disabled={create.isPending}>
            Apply increase
          </Button>
        </form>

        {indexations && indexations.length > 0 ? (
          <ul className="space-y-1.5 text-sm">
            {indexations.map((i) => (
              <li
                key={i.id}
                className="flex items-center justify-between border-b border-border/60 pb-1.5 tabular-nums last:border-0"
              >
                <span className="text-muted-foreground">{i.effective_date}</span>
                <span>
                  {euro(i.old_rent)} → {euro(i.new_rent)} (+{i.percentage}%)
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No indexations yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
